import React, { useCallback, useEffect, useState } from 'react';
import { Search, ChevronLeft, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import dayjs from 'dayjs';
import { adminApi, adminErrorMsg } from '../../admin/adminApiClient';
import { usePermissions } from '../../admin/usePermissions';
import { toast } from 'sonner';

const ROLES = ['all', 'customer', 'organizer'];
const STATUSES = ['all', 'active', 'inactive', 'blocked', 'suspended'];
const ACTIONS = ['activate', 'deactivate', 'block', 'suspend', 'delete'];
const STATUS_STYLE = {
  active: 'bg-emerald-50 text-emerald-700',
  inactive: 'bg-gray-100 text-gray-600',
  blocked: 'bg-red-50 text-red-600',
  suspended: 'bg-amber-50 text-amber-700',
};

export default function AdminUsersPage() {
  const { has } = usePermissions();
  const canManage = has('users.activate') || has('users.deactivate') || has('users.suspend');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('all');
  const [status, setStatus] = useState('all');
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const [actionTarget, setActionTarget] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { limit, offset };
      if (q) params.q = q;
      if (role !== 'all') params.role = role;
      if (status !== 'all') params.status = status;
      const r = await adminApi.get('/users', { params });
      setItems(r.data?.items || r.data || []);
      setTotal(r.data?.total ?? (r.data?.length || 0));
    } catch (e) {
      toast.error(adminErrorMsg(e));
    }
  }, [q, role, status, offset]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const doAction = async () => {
    if (!actionTarget || !actionType) return;
    const destructive = ['block', 'suspend', 'delete'].includes(actionType);
    if (destructive && !actionReason.trim()) { toast.error('Reason is required for this action'); return; }
    if (actionType === 'delete' && !window.confirm('Permanently delete this user?')) return;
    setActing(true);
    try {
      await adminApi.patch(`/users/${actionTarget.id}/status`, {
        action: actionType,
        reason: actionReason.trim() || undefined,
      });
      toast.success(`User ${actionType}d successfully`);
      setActionTarget(null);
      setActionType('');
      setActionReason('');
      await load();
    } catch (e) { toast.error(adminErrorMsg(e)); }
    finally { setActing(false); }
  };

  const pages = Math.ceil(total / limit);
  const page = Math.floor(offset / limit) + 1;

  return (
    <div data-testid="admin-users-page">
      <h1 className="text-xl font-black text-gray-900 mb-4">Users</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400" />
          <input data-testid="users-search" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} placeholder="Search name or email..." className="py-2 text-sm text-gray-900 bg-transparent focus:outline-none flex-1" />
        </div>
        <select data-testid="users-role-filter" value={role} onChange={(e) => { setRole(e.target.value); setOffset(0); }} className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700">
          {ROLES.map((r) => <option key={r} value={r}>{r === 'all' ? 'All Roles' : r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
        </select>
        <select data-testid="users-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }} className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700">
          {STATUSES.map((s) => <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-magenta" /></div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="text-right px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-semibold text-gray-900">{u.full_name}</td>
                      <td className="px-4 py-3 text-gray-600">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-bold text-brand-magenta bg-pink-50 px-2 py-0.5 rounded-full">{u.role}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${STATUS_STYLE[u.status] || 'bg-gray-100 text-gray-600'}`}>
                          {(u.status || 'active').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{dayjs(u.created_at).format('MMM D, YYYY')}</td>
                      <td className="px-4 py-3 text-right">
                        {canManage && (
                        <button
                          data-testid={`user-action-${u.id}`}
                          onClick={() => { setActionTarget(u); setActionType(''); setActionReason(''); }}
                          className="text-xs font-bold text-brand-magenta hover:text-pink-700"
                        >
                          Manage
                        </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {items.length === 0 && (
              <p className="text-center text-sm text-gray-400 py-10">No users found</p>
            )}
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-gray-500">{total} total · Page {page} of {pages}</p>
              <div className="flex gap-1">
                <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
                <button disabled={page >= pages} onClick={() => setOffset(offset + limit)} className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Action Dialog */}
      {actionTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setActionTarget(null)} />
          <div className="relative bg-white rounded-xl w-full max-w-md p-6 mx-4 shadow-xl">
            <h3 className="text-base font-bold text-gray-900 mb-1">Manage User</h3>
            <p className="text-sm text-gray-500 mb-4">{actionTarget.full_name} ({actionTarget.email})</p>

            <label className="text-xs font-bold text-gray-500 mb-1 block">Action</label>
            <select data-testid="user-action-select" value={actionType} onChange={(e) => setActionType(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3">
              <option value="">Select action...</option>
              {ACTIONS.map((a) => <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>)}
            </select>

            {['block', 'suspend', 'delete'].includes(actionType) && (
              <>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Reason</label>
                <input data-testid="user-action-reason" value={actionReason} onChange={(e) => setActionReason(e.target.value)} placeholder="Reason for this action" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3" />
              </>
            )}

            <div className="flex gap-2 mt-2">
              <button onClick={() => setActionTarget(null)} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
              <button data-testid="user-action-confirm" onClick={doAction} disabled={!actionType || acting} className="flex-1 py-2.5 rounded-lg bg-brand-magenta text-white text-sm font-semibold disabled:opacity-50 hover:bg-pink-700 transition-colors">
                {acting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
